import React, { FC, useEffect, useMemo, useState } from "react";
import Heading from "components/Heading/Heading";
import Glide from "@glidejs/glide";
import { TaxonomyType } from "data/types";
import CardCategory3 from "components/CardCategory3/CardCategory3";
import CardCategory4 from "components/CardCategory4/CardCategory4";
import CardCategory5 from "components/CardCategory5/CardCategory5";
import NextPrev from "shared/NextPrev/NextPrev";
import useNcId from "hooks/useNcId";
import CarrouselCard from "components/CarrouselCard/CarrouselCard";
import api from "../../api";

export interface SectionSliderNewCategoriesProps {
  className?: string;
  itemClassName?: string;
  heading?: string;
  subHeading?: string;
  categories?: TaxonomyType[];
  categoryCardType?: "card3" | "card4" | "card5" | "carousel";
  itemPerRow?: 4 | 5;
  sliderStyle?: "style1" | "style2";
  uniqueClassName: string;
  sessionUser?: string;
  userType?: 'agency' | 'brand';
  useScoring?: boolean;
  limit?: number;
}

const SectionSliderNewCategories: FC<SectionSliderNewCategoriesProps> = ({
  heading = "Heading of sections",
  subHeading = "Descriptions for sections",
  className = "",
  itemClassName = "",
  categories = [],
  itemPerRow = 5,
  categoryCardType = "card3",
  sliderStyle = "style1",
  uniqueClassName,
  sessionUser,
  userType,
  useScoring = true,
  limit = 10,
}) => {
  const UNIQUE_CLASS = "SectionSliderNewCategories__" + uniqueClassName + useNcId();
  const [carouselData, setCarouselData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [glideInstance, setGlideInstance] = useState<Glide | null>(null);

  useEffect(() => {
    if (categoryCardType === "carousel" && sessionUser && userType) {
      fetchCarouselData();
    }
  }, [categoryCardType, sessionUser, userType, useScoring, limit]);

  const fetchCarouselData = () => {
    setLoading(true);
    setError("");

    const parsedSessionUser = parseInt(sessionUser);
    if (isNaN(parsedSessionUser) || parsedSessionUser <= 0) {
      setError("Invalid session user ID");
      setLoading(false);
      return;
    }

    const payload = {
      sessionUser: parsedSessionUser,
      userType,
      limit,
    };

    const apiMethod = useScoring 
      ? api.getCarouselMatchesWithScoring 
      : api.getCarouselMatches;

    apiMethod(
      payload,
      (response) => {
        setLoading(false);
        
        if (response.status === 200 && response.data?.data) {
          const sortedData = [...response.data.data].sort((a, b) => {
            const nameA = (a.name || '').toLowerCase();
            const nameB = (b.name || '').toLowerCase();
            return nameA.localeCompare(nameB);
          });
          
          setCarouselData(sortedData);
          
          if (sortedData.length === 0) {
            setError("No matching agencies found based on your categories and regions.");
          }
        } else {
          setError("Failed to load carousel data");
        }
      },
      (error) => {
        setLoading(false);
        setError(error.message || "Error loading carousel data");
      }
    );
  };

  let MY_GLIDEJS = useMemo(() => {
    return new Glide(`.${UNIQUE_CLASS}`, {
      perView: itemPerRow,
      gap: 32,
      bound: true,
      breakpoints: {
        1280: {
          perView: itemPerRow - 1,
        },
        1024: {
          gap: 20,
          perView: itemPerRow - 1,
        },
        768: {
          gap: 20,
          perView: itemPerRow - 2,
        },
        640: {
          gap: 20,
          perView: itemPerRow - 3,
        },
        500: {
          gap: 20,
          perView: 1.3,
        },
      },
    });
  }, [UNIQUE_CLASS, itemPerRow]);

  useEffect(() => {
    if (categoryCardType !== "carousel" || carouselData.length > 0) {
      const timer = setTimeout(() => {
        try {
          MY_GLIDEJS.mount();
          setGlideInstance(MY_GLIDEJS);
        } catch (error) {
          console.error("Glide mount error:", error);
        }
      }, 100);

      return () => {
        clearTimeout(timer);
        if (MY_GLIDEJS) {
          try {
            MY_GLIDEJS.destroy();
          } catch (error) {
            console.error("Glide destroy error:", error);
          }
        }
      };
    }
  }, [MY_GLIDEJS, carouselData, categoryCardType]);

  const renderCard = (item: any, index: number) => {
    if (categoryCardType === "carousel") {
      return (
        <CarrouselCard
          key={item.id}
          data={item}
        />
      );
    }

    switch (categoryCardType) {
      case "card3":
        return <CardCategory3 key={index} taxonomy={item} />;
      case "card4":
        return <CardCategory4 key={index} taxonomy={item} />;
      case "card5":
        return <CardCategory5 key={index} taxonomy={item} />;
      default:
        return <CardCategory3 key={index} taxonomy={item} />;
    }
  };

  const renderSliderGallery = () => {
    const dataToRender = categoryCardType === "carousel" ? carouselData : categories;
    
    return (
      <div className="glide__track" data-glide-el="track">
        <ul className="glide__slides">
          {dataToRender.map((item, index) => (
            <li key={item.id || index} className={`glide__slide ${itemClassName}`}>
              {renderCard(item, index)}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderSliderContent = () => {
    const dataToRender = categoryCardType === "carousel" ? carouselData : categories;
    const showSideNavigation = categoryCardType === "carousel" && dataToRender.length > itemPerRow;

    return (
      <div className="flow-root">
        {showSideNavigation ? (
          <div className="relative">
            <button
              className="absolute left-0 top-1/2 -translate-y-1/2 z-30 bg-white shadow-lg rounded-full p-3 hover:bg-gray-50 transition-colors border border-gray-200"
              onClick={() => glideInstance?.go('<')}
              data-glide-dir="<"
              style={{
                transform: 'translateX(-50%) translateY(-50%)',
                left: '-30px'
              }}
            >
              <svg 
                className="w-5 h-5 text-gray-600" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M15 19l-7-7 7-7" 
                />
              </svg>
            </button>

            {renderSliderGallery()}

            <button
              className="absolute right-0 top-1/2 -translate-y-1/2 z-30 bg-white shadow-lg rounded-full p-3 hover:bg-gray-50 transition-colors border border-gray-200"
              onClick={() => glideInstance?.go('>')}
              data-glide-dir=">"
              style={{
                transform: 'translateX(50%) translateY(-50%)',
                right: '-30px'
              }}
            >
              <svg 
                className="w-5 h-5 text-gray-600" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M9 5l7 7-7 7" 
                />
              </svg>
            </button>
          </div>
        ) : (
          renderSliderGallery()
        )}

        {sliderStyle === "style2" && categoryCardType !== "carousel" && (
          <NextPrev className="justify-center mt-16" />
        )}
      </div>
    );
  };

  const renderLoadingState = () => (
    <div className="flex justify-center items-center py-16">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      <span className="ml-3 text-gray-600">Loading matches...</span>
    </div>
  );

  const renderErrorState = () => (
    <div className="flex flex-col items-center justify-center py-16">
      <div className="text-center">
        <div className="text-gray-400 mb-4">
          <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h3 className="text-sm font-medium text-gray-900 mb-2">
          No matches found
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          {error}
        </p>
        <button
          onClick={fetchCarouselData}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Try Again
        </button>
      </div>
    </div>
  );

  const renderEmptyState = () => (
    <div className="flex flex-col items-center justify-center py-16">
      <div className="text-center">
        <div className="text-gray-400 mb-4">
          <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <h3 className="text-sm font-medium text-gray-900 mb-2">
          No {userType === 'agency' ? 'brands' : 'agencies'} found
        </h3>
        <p className="text-sm text-gray-500">
          We couldn't find any {userType === 'agency' ? 'brands' : 'agencies'} matching your categories and regions.
        </p>
      </div>
    </div>
  );

  if (categoryCardType === "carousel" && (!sessionUser || !userType)) {
    return null;
  }

  return (
    <div className={`nc-SectionSliderNewCategories ${className}`}>
      <div className={`${UNIQUE_CLASS} ${categoryCardType === "carousel" ? "carousel-equal-height" : ""} flow-root`}>
        <Heading 
          desc={subHeading} 
          hasNextPrev={
            sliderStyle === "style1" && categoryCardType !== "carousel"
          }
          isCenter={sliderStyle === "style2" && categoryCardType !== "carousel"}
        >
          {heading}
        </Heading>

        {categoryCardType === "carousel" ? (
          <>
            {loading && renderLoadingState()}
            {error && !loading && renderErrorState()}
            {!loading && !error && carouselData.length === 0 && renderEmptyState()}
            {!loading && !error && carouselData.length > 0 && renderSliderContent()}
          </>
        ) : (
          renderSliderContent()
        )}
      </div>
    </div>
  );
};

export default SectionSliderNewCategories;